import useMessagingFilters from "@/composables/useMessagingFilters";
import axios from "@/libs/axios";
import { useChannelsStore } from "@/store/useChannelsStore";
import { Team, TeamsUsersTree } from "@/types/team.types";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { useQuery } from "@tanstack/vue-query";
import { AxiosResponse } from "axios";
import { defineStore } from "pinia";

interface State {
    isLoadingTeams: boolean;
    teams: TeamsUsersTree[];
}

export const useTeamsStore = defineStore("teams", {
    state: (): State => ({
        isLoadingTeams: false,
        teams: [],
    }),
    actions: {
        fetchTeams(requestQueryBuilder?: RequestQueryBuilder, forceRefetch = false): void {
            useQuery<Pick<AxiosResponse<TeamsUsersTree[]>, "data">>(
                ["teams", requestQueryBuilder?.queryObject, forceRefetch],
                () => {
                    this.isLoadingTeams = true;
                    return axios.get<TeamsUsersTree[]>(`${process.env.VUE_APP_PUBLIC_API}/teams`, {
                        params: requestQueryBuilder?.queryObject,
                    });
                },
                {
                    staleTime: forceRefetch ? 0 : 300000, // 5min
                    placeholderData: { data: [] },
                    onSuccess: (response) => (this.teams = response.data),
                    onSettled: () => (this.isLoadingTeams = false),
                }
            );
        },
    },
    getters: {
        getRestrictedTeams(state): () => Team[] {
            return () => {
                const { enforcedTeamFilters } = useMessagingFilters();
                return enforcedTeamFilters.value.enabled ? enforcedTeamFilters.value.teams : state.teams;
            };
        },
        getTeamById(state): (teamId: number) => Team | undefined {
            return (teamId) => state.teams.find((team) => team.id === teamId);
        },
        isUserPartOfTeam(state): (userId: number, teamId: number) => boolean | undefined {
            return (userId, teamId): boolean | undefined => {
                if (!userId || !teamId) {
                    return;
                }

                return state.teams
                    .find((team) => team.id === teamId)
                    ?.teamUsers.some((teamUser) => teamUser.userId === userId);
            };
        },
        getRandomUserTeamForChannel(state): (userId: number, channelId: number) => number | undefined {
            // Mostly copy-pasted from old composable with slight modifications. Might be done differently in the future.
            return (userId, channelId): number | undefined => {
                if (!userId || !channelId) {
                    return;
                }

                const { channels } = useChannelsStore();
                const channel = channels.find((channel) => channel.id === channelId);

                if (!channel?.teamChannels || state.teams.length === 0) {
                    return;
                }

                return state.teams.find(
                    (team) =>
                        channel.teamChannels.some((teamChannel) => teamChannel.id === team.id) &&
                        team.teamUsers.some((teamUser) => teamUser.userId === userId)
                )?.id;
            };
        },
    },
});
